/**
 * Posts Slider Block JavaScript
 */

import { isBlockOnPage, whenVisible } from "../../src/js/modules/block-present.js";

(function () {
  "use strict";

  function initPostsSlider() {
    if (
      typeof gsap === "undefined" ||
      typeof Draggable === "undefined" ||
      typeof InertiaPlugin === "undefined"
    ) {
      return;
    }

    gsap.registerPlugin(Draggable, InertiaPlugin);

    const blocks = document.querySelectorAll(".posts-slider");

    blocks.forEach((block) => {
      const container = block.querySelector(".posts-slider-container");
      if (!container) return;

      new DraggablePostsSlider(container);
    });
  }

  class DraggablePostsSlider {
    constructor(container) {
      this.container = container;
      this.block = container.closest(".posts-slider");
      this.guide = container.querySelector(".posts-slider-guide");
      this.slider = container.querySelector(".posts-slider-track");
      this.slides = [...container.querySelectorAll(".posts-slider-item")];
      this.slidesNumb = this.slides.length;
      this.sliderWidth = 0;
      this.dragMultiplier = 1.5;
      this.maxDragX = 0;
      this.didDrag = false;
      this.dragThreshold = 5;
      this.dragStartX = 0;
      this.paginationEl = this.block
        ? this.block.querySelector(".posts-slider-pagination")
        : null;
      this.paginationDotClass = "posts-slider-pagination__dot";
      this.mobilePaginationMql = null;
      this._paginationRaf = null;

      if (!this.guide || !this.slider || this.slides.length === 0) {
        return;
      }

      window.addEventListener("resize", () => this.calculateBounds());

      this.init();
      this.initClickPrevent();
      if (this.paginationEl) {
        this.initPagination();
      }
    }

    calculateBounds() {
      const viewportWidth = this.container.offsetWidth;
      const trackStyle = window.getComputedStyle(this.slider);
      const padL = parseFloat(trackStyle.paddingLeft) || 0;
      const padR = parseFloat(trackStyle.paddingRight) || 0;
      const gap = parseFloat(trackStyle.gap) || 0;

      let totalWidth = padL + padR;
      this.slides.forEach((slide, i) => {
        const style = window.getComputedStyle(slide);
        const marginLeft = parseFloat(style.marginLeft) || 0;
        const marginRight = parseFloat(style.marginRight) || 0;
        totalWidth += slide.offsetWidth + marginLeft + marginRight;
        if (i < this.slides.length - 1) {
          totalWidth += gap;
        }
      });

      this.sliderWidth = totalWidth;

      const maxSliderX = -(totalWidth - viewportWidth);
      this.maxDragX = maxSliderX / this.dragMultiplier;

      if (totalWidth <= viewportWidth) {
        this.maxDragX = 0;
      }

      gsap.set(this.slider, {
        width: totalWidth,
        x: 0,
      });

      gsap.set(this.guide, {
        width: totalWidth,
        x: 0,
      });

      if (this.draggable) {
        this.draggable.applyBounds({ minX: this.maxDragX, maxX: 0 });
        if (this.draggable.x < this.maxDragX) {
          gsap.set(this.guide, { x: this.maxDragX });
          gsap.set(this.slider, { x: this.maxDragX * this.dragMultiplier });
        } else if (this.draggable.x > 0) {
          gsap.set(this.guide, { x: 0 });
          gsap.set(this.slider, { x: 0 });
        }
      }

      if (this.paginationEl) {
        this.syncPaginationUi();
      }
    }

    initPagination() {
      this.mobilePaginationMql = window.matchMedia("(max-width: 767px)");
      this.mobilePaginationMql.addEventListener("change", () =>
        this.syncPaginationUi(),
      );
      this.syncPaginationUi();
    }

    syncPaginationUi() {
      if (!this.paginationEl || !this.mobilePaginationMql) {
        return;
      }
      const show =
        this.mobilePaginationMql.matches &&
        this.slides.length > 1 &&
        this.sliderWidth > this.container.offsetWidth;
      if (show) {
        this.paginationEl.hidden = false;
        this.paginationEl.classList.add("is-visible");
        this.buildPaginationDots();
        this.updatePaginationActive();
      } else {
        this.paginationEl.hidden = true;
        this.paginationEl.classList.remove("is-visible");
        this.paginationEl.replaceChildren();
      }
    }

    buildPaginationDots() {
      if (!this.paginationEl) {
        return;
      }
      const existing = this.paginationEl.querySelectorAll(
        `.${this.paginationDotClass}`,
      ).length;
      if (existing === this.slides.length) {
        return;
      }
      this.paginationEl.replaceChildren();
      this.slides.forEach((_, i) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = this.paginationDotClass;
        btn.setAttribute(
          "aria-label",
          `Slide ${i + 1} of ${this.slides.length}`,
        );
        btn.addEventListener("click", () => this.goToSlide(i));
        this.paginationEl.appendChild(btn);
      });
    }

    /** Guide-space snap targets (one per slide), same geometry as goToSlide. */
    getSnapGuideXs() {
      if (this.slides.length === 0) {
        return [0];
      }
      const maxS = this.maxDragX * this.dragMultiplier;
      return this.slides.map((slide) => {
        let targetSliderX = -slide.offsetLeft;
        targetSliderX = Math.max(maxS, Math.min(0, targetSliderX));
        return targetSliderX / this.dragMultiplier;
      });
    }

    getActiveSlideIndex() {
      const x = parseFloat(gsap.getProperty(this.slider, "x")) || 0;
      let best = 0;
      let bestDist = Infinity;
      this.slides.forEach((slide, i) => {
        const dist = Math.abs(slide.offsetLeft + x);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    }

    schedulePaginationActiveUpdate() {
      if (!this.paginationEl || !this.mobilePaginationMql?.matches) {
        return;
      }
      if (this._paginationRaf != null) {
        return;
      }
      this._paginationRaf = requestAnimationFrame(() => {
        this._paginationRaf = null;
        this.updatePaginationActive();
      });
    }

    updatePaginationActive(index) {
      if (
        !this.paginationEl ||
        !this.paginationEl.classList.contains("is-visible")
      ) {
        return;
      }
      const i = index === undefined ? this.getActiveSlideIndex() : index;
      const dots = this.paginationEl.querySelectorAll(
        `.${this.paginationDotClass}`,
      );
      dots.forEach((dot, di) => {
        const on = di === i;
        dot.classList.toggle("is-active", on);
        if (on) {
          dot.setAttribute("aria-current", "true");
        } else {
          dot.removeAttribute("aria-current");
        }
      });
    }

    goToSlide(index) {
      if (index < 0 || index >= this.slides.length) {
        return;
      }
      const slide = this.slides[index];
      let targetSliderX = -slide.offsetLeft;
      const maxS = this.maxDragX * this.dragMultiplier;
      targetSliderX = Math.max(maxS, Math.min(0, targetSliderX));
      const targetGuideX = targetSliderX / this.dragMultiplier;

      gsap.killTweensOf([this.guide, this.slider]);
      gsap.to(this.guide, {
        x: targetGuideX,
        duration: 0.45,
        ease: "power2.out",
        onUpdate: () => {
          if (this.draggable) {
            this.draggable.update();
          }
        },
        onComplete: () => {
          if (this.draggable) {
            this.draggable.update();
          }
        },
      });
      gsap.to(this.slider, {
        x: targetSliderX,
        duration: 0.45,
        ease: "power2.out",
      });
      this.updatePaginationActive(index);
    }

    createDraggable() {
      let slider = this.slider;
      let dragMultiplier = this.dragMultiplier;
      const self = this;

      // Slider must track the guide 1:1 during drag/throw. A delayed tween on desktop
      // (previously 0.8s) left the slides catching up after release and felt broken.
      const syncSliderToGuide = (guideX) => {
        const newX = guideX * dragMultiplier;
        gsap.set(slider, { x: newX });
      };

      const onSwipeFinish = () => {
        setTimeout(() => {
          if (
            self.paginationEl &&
            self.mobilePaginationMql?.matches
          ) {
            self.updatePaginationActive();
          }
        }, 0);
      };

      this.draggable = Draggable.create(this.guide, {
        type: "x",
        trigger: this.container,
        edgeResistance: 0.75,
        bounds: { minX: this.maxDragX, maxX: 0 },
        minimumMovement: 10,
        inertia: true,
        throwProps: true,
        liveSnap: false,
        snap: {
          x: function (endValue) {
            const points = self.getSnapGuideXs();
            if (points.length < 2) {
              return endValue;
            }
            return gsap.utils.snap(points, endValue);
          },
        },
        onDragStart: function () {
          self.didDrag = false;
          self.dragStartX = this.x;
          gsap.killTweensOf(slider);
        },
        onDrag: function () {
          if (Math.abs(this.x - self.dragStartX) > self.dragThreshold) {
            self.didDrag = true;
          }
          syncSliderToGuide(this.x);
          self.schedulePaginationActiveUpdate();
        },
        onThrowUpdate: function () {
          syncSliderToGuide(this.x);
          self.schedulePaginationActiveUpdate();
        },
        onDragEnd: function () {
          setTimeout(function () {
            self.didDrag = false;
          }, 0);
          onSwipeFinish();
        },
        onThrowComplete: onSwipeFinish,
      })[0];
    }

    initClickPrevent() {
      this.container.addEventListener(
        "click",
        (e) => {
          if (this.didDrag) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        true
      );
    }

    init() {
      this.calculateBounds();
      this.createDraggable();
    }
  }

  function checkAndInit() {
    if (
      typeof gsap !== "undefined" &&
      typeof Draggable !== "undefined" &&
      typeof InertiaPlugin !== "undefined"
    ) {
      initPostsSlider();
    } else {
      setTimeout(checkAndInit, 100);
    }
  }

  function bootPostsSlider() {
    if (!isBlockOnPage("posts-slider")) {
      return;
    }

    whenVisible(".posts-slider", checkAndInit, { rootMargin: "400px 0px" });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootPostsSlider);
  } else {
    bootPostsSlider();
  }
})();